import React, { useState } from 'react';
import styled from 'styled-components';

import {
  Button,
  Card,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  AssignmentReturned as AssignmentReturnedIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { Alert } from '@material-ui/lab';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ChangeOrder } from '../ChangeOrder.interface';
import AssignChangeOrderDialog from '../dialogs/AssignChangeOrderDialog';

const StyledActionTableCell = styled(TableCell)`
  .MuiButtonBase-root {
    &:not(:last-child) {
      margin-right: 8px;
    }
  }
`;

const columns: { key: string; label: string }[] = [
  { key: 'changeOrder.id', label: 'ID' },
  { key: 'changeOrder.name', label: 'Name' },
  { key: 'task.status', label: 'Status' }
];

export interface ChangeOrderReferenceCardProps {
  items: ChangeOrder[] | undefined;
  disabled?: boolean;
  isLoading?: boolean;
  error?: FetchError;
  direction?: 'ASC' | 'DESC';
  handleSort?: (column: string) => void;
  isActive?: (column: string) => boolean;
  onRemove?: (changeOrder: ChangeOrder) => void;
  onAssign?: (changeOrders: ChangeOrder[]) => void;
}

const ChangeOrderReferenceCard: React.FC<
  ChangeOrderReferenceCardProps
> = props => {
  const {
    items,
    disabled,
    direction,
    isLoading,
    error,
    handleSort,
    isActive,
    onRemove,
    onAssign
  } = props;
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [removeItem, setRemoveItem] = useState<ChangeOrder>();
  const colSpan = columns.length + 1;

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return isActive?.(column) && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <Card>
      <CardTitle>
        <AssignmentReturnedIcon />
        Change Order References
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.key}>
                  <TableSortLabel
                    active={isActive?.(column.key)}
                    direction={getSortDirection(column.key)}
                    onClick={() => handleSort?.(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colSpan} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {(!items || items.length === 0) && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  {isLoading ? 'Loading...' : 'No items assigned.'}
                </TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell padding='none' colSpan={colSpan}>
                  <Alert severity='error'>{error.message}</Alert>
                </TableCell>
              </TableRow>
            )}
            {items?.map((item, index) => (
              <TableRow
                hover
                color='primary'
                key={`${item.id}-${item.task.key}-${index}`}
                onClick={() => {
                  history.push(`/portal/change-order/task/${item.task.id}`);
                }}
              >
                <TableCell>{item.task.key}</TableCell>
                <WrappedTableCell>{item.name}</WrappedTableCell>
                <TableCell>
                  <TaskStatusLabel task={item.task} />
                </TableCell>
                <TableCell padding='checkbox'>
                  <IconButton
                    disabled={disabled}
                    onClick={event => {
                      event.stopPropagation();
                      setRemoveItem(item);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <StyledActionTableCell colSpan={colSpan}>
                <Button
                  color='primary'
                  variant='outlined'
                  disabled={disabled}
                  onClick={() => setOpen(true)}
                >
                  Assign Change Orders
                </Button>
              </StyledActionTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        open={!!removeItem}
        title='Unlink Change Order'
        message={`Are you sure you want to unlink ${
          removeItem?.task.key || '?'
        }?`}
        onCancel={() => setRemoveItem(undefined)}
        onConfirm={() => {
          setRemoveItem(undefined);
          if (removeItem) {
            onRemove?.(removeItem);
          }
        }}
      />
      <AssignChangeOrderDialog
        open={open}
        items={items}
        onCancel={() => setOpen(false)}
        onAssign={items => {
          onAssign?.(items);
          setOpen(false);
        }}
      />
    </Card>
  );
};

export default ChangeOrderReferenceCard;
